import React, { Fragment } from 'react'
import Table from 'UI/Table'
import { get, sumBy, presence } from 'lodash'
import Loadable from 'react-loadable'

import { filteredVMData, groupedData } from './Data'

const SoftwareSummaryTable = ({ filters, data, showNonSummarisedData, groupByField, SubComponent }) => {
  return (
    <Table {...{
      data: groupedData(groupByField, filteredVMData({ vms: data, filters })),
      columns: getColumns(groupByField, showNonSummarisedData),
      filterable: true,
      showPagination: false,
      defaultPageSize: data.length,
      SubComponent
    }} />
  )
}

const VMTable = Loadable({
  loader: () => import('./VMTable'),
  loading: () => <div>Loading...</div>
})

const ApplicationTable = Loadable({
  loader: () => import('./Applications/ApplicationTable'),
  loading: () => <div>Loading...</div>
})

const getSubComponent = ({ filters, showNonSummarisedData, fieldToGroupOn }) => {
  if (filters.currentTab === 'ms-operatingSystems') {
    if (presence(fieldToGroupOn, filters.groupByField) === 'Customer') {
      return row => (
        <SoftwareSummaryTable {...{
          data: row.original.softwares,
          filters,
          showNonSummarisedData,
          groupByField: 'OperatingSystem',
          SubComponent: (row) => <VMTable {...{ data: row.original.softwares }} /> }}
        />
      )
    } else if (presence(fieldToGroupOn, filters.groupByField) === 'OperatingSystem') {
      return row => <SoftwareSummaryTable {...{ data: row.original.softwares, filters, showNonSummarisedData, groupByField: 'Customer', SubComponent: (row) => <VMTable {...{ data: row.original.softwares }} /> }} />
    }
  }

  if (filters.currentTab === 'ms-applications' || filters.currentTab === 'SQL') {
    if (presence(fieldToGroupOn, filters.groupByField) === 'Customer') {
      return row => <SoftwareSummaryTable {...{ data: row.original.softwares, filters, showNonSummarisedData, groupByField: 'Application', SubComponent: (row) => <ApplicationTable {...{ data: row.original.softwares }} /> }} />
    } else if (presence(fieldToGroupOn, filters.groupByField) === 'Application') {
      return row => <SoftwareSummaryTable {...{ data: row.original.softwares, filters, showNonSummarisedData, groupByField: 'Customer', SubComponent: (row) => <ApplicationTable {...{ data: row.original.softwares }} /> }} />
    }
  }

  return (row) => <SoftwareSummaryTable {...{ data: row.original.softwares, filters, showNonSummarisedData, groupByField: filters.groupByField }} />
}

const vmColumns = [
  {
    Header: 'Total Assets',
    id: 'total_assets',
    style: { textAlign: 'center' },
    accessor: ({ softwares }) => softwares.length,
    Footer: ({ data }) => (
      <Fragment>
        <strong>Total Assets: </strong>{sumBy(data, datum => parseInt(get(datum, 'total_assets'))).toLocaleString()}
      </Fragment>
    )
  },
]

const summaryButton = (group, value, showNonSummarisedData, label = null) => (
  <button className='button__link' onClick={() => showNonSummarisedData(group, value)}>
    {label || value}
  </button>
)

const getColumns = (groupKey, showNonSummarisedData) => get({
  Cluster: [
    { Header: 'Location', accessor: 'location' },
    { Header: 'Cluster', accessor: 'cluster.name', Cell: (data) => summaryButton('clusters', data.original.cluster.id, showNonSummarisedData, data.value) },
    { Header: 'Function', accessor: 'cluster.function' },
    { Header: 'Ownership', accessor: 'cluster.ownership' },
    { Header: 'Cores', accessor: 'cluster.summed_cores', type: 'number', Footer: ({ data }) => {
      const summedValue = sumBy(data, datum => {
        const summedCores = get(datum, 'cluster.summed_cores')
        const value = summedCores === 'N/A' ? 0 : summedCores
        return parseInt(value)
      })

      return (
        <Fragment>
          <strong>Total Cores: </strong>{summedValue.toLocaleString() || 0}
        </Fragment>
      )
    }},
    {
      Header: 'Sockets', accessor: 'cluster.summed_sockets', type: 'number', Footer: ({ data }) => {
        const summedValue = sumBy(data, datum => {
          const summedSockets = get(datum, 'cluster.summed_sockets')
          const value = summedSockets === 'N/A' ? 0 : summedSockets
          return parseInt(value)
        })

        return (
          <Fragment>
            <strong>Total Sockets: </strong>{summedValue.toLocaleString() || 0}
          </Fragment>
        )
      }},
    ...vmColumns
  ],
  OperatingSystem: [
    { Header: 'Operating System', accessor: 'operating_system', Cell: ({ value }) => summaryButton('operating_systems', value, showNonSummarisedData) },
    ...vmColumns
  ],
  Application: [
    { Header: 'Application', accessor: 'application', Cell: ({ value }) => summaryButton('applications', value, showNonSummarisedData) },
    ...vmColumns
  ],
  Vendor: [
    { Header: 'Vendor', accessor: 'location', Cell: ({ value }) => summaryButton('vendors', value, showNonSummarisedData) },
    ...vmColumns
  ],
  Customer: [
    { Header: 'Customer', accessor: 'customer', Cell: ({ value }) => summaryButton('customers', value, showNonSummarisedData) },
    ...vmColumns
  ],
}, groupKey, [])

const SummaryTable = ({ filters, data, showNonSummarisedData }) => {
  if (filters.groupByField === 'Cluster') {
    return (
      <Table {...{
        data: groupedData(filters.groupByField, filteredVMData({ vms: data, filters })),
        columns: getColumns(filters.groupByField, showNonSummarisedData),
        filterable: true,
        SubComponent: (row) => (
          <SoftwareSummaryTable {...{
            filters,
            data: row.original.softwares,
            showPagination: false,
            defaultPageSize: row.original.softwares.length,
            showNonSummarisedData,
            groupByField: 'OperatingSystem',
            SubComponent: (row) => <VMTable {...{ data: row.original.softwares }} />
          }} />
        )
      }} />
    )
  }

  return <SoftwareSummaryTable {...{
    filters,
    data,
    showNonSummarisedData,
    groupByField: filters.groupByField,
    SubComponent: getSubComponent({ filters, showNonSummarisedData })
  }}/>
}

export default SummaryTable
